// The console frame: environment band, rail (desktop/tablet) or top + bottom
// bars (mobile), global search, and the range control on insight pages.

import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import type { AdminMe } from "@paceroo/domain/views/admin";
import { IconBook, IconChart, IconClose, IconDial, IconFlag, IconGrip, IconPrinter, IconSearch, IconStride } from "../../components/Icons";
import { PaceLockup, PaceLogo } from "../../components/Logos";
import { fT } from "../lib/format";
import { useBreakpoint, type Breakpoint } from "../lib/useBreakpoint";
import { rangeQuery, useRange } from "../lib/useRange";
import { ShellContext, type ShellApi } from "./context";
import { RangeControl } from "./RangeControl";
import { Search } from "./Search";
import s from "./Shell.module.css";
import { useRunnerSearch } from "./useRunnerSearch";

type IconCmp = ComponentType<{ size?: number }>;

const NAV: Array<{ to: string; label: string; short: string; Icon: IconCmp; insight: boolean }> = [
  { to: "/", label: "Overview", short: "Overview", Icon: IconChart, insight: true },
  { to: "/runners", label: "Runners", short: "Runners", Icon: IconStride, insight: false },
  { to: "/exports", label: "Exports", short: "Exports", Icon: IconPrinter, insight: true },
  { to: "/assistants", label: "AI assistants", short: "AI", Icon: IconDial, insight: true },
  { to: "/events", label: "Events", short: "Events", Icon: IconBook, insight: true },
  { to: "/audit", label: "Audit log", short: "Audit", Icon: IconFlag, insight: false },
];

function sectionOf(pathname: string) {
  return NAV.find((n) => (n.to === "/" ? pathname === "/" : pathname === n.to || pathname.startsWith(`${n.to}/`))) ?? NAV[0]!;
}

function envBandText(me: AdminMe, bp: Breakpoint): string {
  const host = window.location.host;
  if (me.environment === "prod") return bp === "mobile" ? "Production · real runners" : `Production · ${host} · actions reach real runners`;
  const label = me.environment === "local" ? "Local" : "Dev";
  return bp === "mobile" ? `${label} · not production` : `${label} · ${host} · ${__APP_VERSION__} · not production`;
}

export function Shell({ me }: { me: AdminMe }) {
  const bp = useBreakpoint();
  const location = useLocation();
  const range = useRange();
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [mSearch, setMSearch] = useState(false);
  const [updated, setUpdated] = useState<Date | null>(null);
  const [toastText, setToastText] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const toast = useCallback((text: string) => {
    window.clearTimeout(toastTimer.current);
    setToastText(text);
    toastTimer.current = window.setTimeout(() => setToastText(null), 6000);
  }, []);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const section = sectionOf(location.pathname);
  const rq = rangeQuery(range);
  const isMobile = bp === "mobile";
  const wide = bp === "desktop";

  // "/" focuses search from anywhere outside a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (e.key !== "/" || el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return;
      e.preventDefault();
      if (isMobile) setMSearch(true);
      else searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile]);

  // New page → top of the scroll container, sheets closed.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    setMoreOpen(false);
    setMSearch(false);
  }, [location.pathname]);

  const focusSearch = useCallback(() => (isMobile ? setMSearch(true) : searchRef.current?.focus()), [isMobile]);
  const api = useMemo<ShellApi>(() => ({ me, bp, focusSearch, setUpdated, toast }), [me, bp, focusSearch, toast]);
  const pad = isMobile ? "20px 16px 40px" : wide ? "32px 48px 72px" : "24px 24px 56px";
  const initials = me.admin.name
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <ShellContext.Provider value={api}>
      <div className={s.root}>
        <div className={clsx(s.band, me.environment === "prod" ? s.bandProd : s.bandDev)} role="status">
          {me.environment === "prod" && <span className={s.bandBullet} />}
          <span>{envBandText(me, bp)}</span>
        </div>

        <div className={s.body}>
          {!isMobile && (
            <nav className={clsx(s.rail, wide ? s.railWide : s.railNarrow)} aria-label="Admin">
              <div className={s.railHead}>
                {wide ? (
                  <>
                    <PaceLockup scale={0.8} />
                    <span className={clsx("kicker ink-mute", s.adminTag)}>Admin</span>
                  </>
                ) : (
                  <PaceLogo size={18} />
                )}
              </div>
              <div className={s.railNav}>
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.insight ? `${n.to}${rq}` : n.to}
                    end={n.to === "/"}
                    title={n.label}
                    className={() => clsx(s.railItem, section.to === n.to && s.railItemOn)}
                  >
                    <n.Icon size={16} />
                    {wide && <span>{n.label}</span>}
                  </NavLink>
                ))}
              </div>
              <div className={s.railSpacer} />
              <div className={s.railFoot}>
                {wide && (
                  <>
                    <span className={s.avatar}>{initials}</span>
                    <div className={s.who}>
                      <span className="t-label">{me.admin.name}</span>
                      <span className={clsx("t-mono-xs ink-mute", s.whoEmail)}>{me.admin.email}</span>
                    </div>
                  </>
                )}
              </div>
            </nav>
          )}

          <div className={s.main}>
            {!isMobile ? (
              <div className={s.topbar} style={{ padding: wide ? "0 48px" : "0 24px" }}>
                <Search ref={searchRef} />
                <div className={s.grow} />
                {section.insight && (
                  <div className={s.rangeGroup}>
                    <RangeControl range={range} />
                    {section.to === "/" && updated && (
                      <span className={clsx("t-mono-xs ink-mute", s.updated)} title="Overview refreshes every 60 s">
                        Updated {fT(updated)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className={s.mTop}>
                  <PaceLogo size={16} />
                  <span className="kicker ink-mute">Admin</span>
                  <div className={s.grow} />
                  <button className={clsx("iconbtn", s.mIconBtn)} onClick={() => setMSearch(true)} aria-label="Search runners">
                    <IconSearch size={18} />
                  </button>
                </div>
                {section.insight && (
                  <div className={clsx("no-scrollbar", s.mRange)}>
                    <RangeControl range={range} compact />
                  </div>
                )}
              </>
            )}

            <div ref={scrollRef} className={s.scroll} data-admin-scroll>
              <div className={s.content} style={{ padding: pad }}>
                <Outlet />
              </div>
            </div>
          </div>
        </div>

        {isMobile && <MobileNav section={section.to} search={rq} onMore={() => setMoreOpen(true)} />}
        {moreOpen && <MoreSheet onClose={() => setMoreOpen(false)} search={rq} />}
        {mSearch && <MobileSearch onClose={() => setMSearch(false)} />}
        {toastText && (
          <div className={s.toast} role="status" style={{ bottom: isMobile ? 76 : 24 }}>
            <span className={s.toastDot} />
            <span className={s.toastText}>{toastText}</span>
            <button className={s.toastClose} onClick={() => setToastText(null)}>
              CLOSE
            </button>
          </div>
        )}
      </div>
    </ShellContext.Provider>
  );
}

function MobileNav({ section, search, onMore }: { section: string; search: string; onMore: () => void }) {
  const items = NAV.slice(0, 4);
  const moreOn = section === "/events" || section === "/audit";
  return (
    <nav className={s.mNav} aria-label="Admin">
      {items.map((n) => (
        <NavLink key={n.to} to={n.insight ? `${n.to}${search}` : n.to} end={n.to === "/"} className={clsx(s.mNavItem, section === n.to && s.mNavItemOn)}>
          <n.Icon size={18} />
          <span>{n.short}</span>
        </NavLink>
      ))}
      <button className={clsx(s.mNavItem, moreOn && s.mNavItemOn)} onClick={onMore}>
        <IconGrip size={18} />
        <span>More</span>
      </button>
    </nav>
  );
}

function MoreSheet({ onClose, search }: { onClose: () => void; search: string }) {
  const navigate = useNavigate();
  return (
    <div className={s.scrim} onClick={onClose}>
      <div className={s.sheet} onClick={(e) => e.stopPropagation()}>
        {NAV.slice(4).map((n) => (
          <button key={n.to} className={s.sheetItem} onClick={() => navigate(n.insight ? `${n.to}${search}` : n.to)}>
            <n.Icon size={18} />
            {n.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function MobileSearch({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState("");
  const { results, searching } = useRunnerSearch(q, 20);
  const navigate = useNavigate();
  return (
    <div className={s.mSearch} role="dialog" aria-label="Search runners">
      <div className={s.mSearchBar}>
        <div className={clsx("input-shell", s.mSearchInput)}>
          <IconSearch size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email, name or id" autoFocus aria-label="Find a runner" />
        </div>
        <button className={clsx("iconbtn", s.mIconBtn)} onClick={onClose} aria-label="Close search">
          <IconClose size={18} />
        </button>
      </div>
      <div className={s.mSearchList}>
        {results.map((r) => (
          <button key={r.id} className={s.mResult} onClick={() => navigate(`/runners/${encodeURIComponent(r.id)}`)}>
            <span className="t-label" style={{ fontSize: 14 }}>
              {r.name}
            </span>
            <span className="t-mono-xs ink-mute">{r.email}</span>
          </button>
        ))}
        {!q.trim() && <p className={clsx("t-body-sm ink-mute", s.mHint)}>Search by email, name or runner id.</p>}
        {q.trim() && !searching && results.length === 0 && <p className={clsx("t-body-sm ink-mute", s.mHint)}>No runner matches “{q.trim()}”.</p>}
      </div>
    </div>
  );
}

/** Shown when /me is refused: not behind Access, or not an admin. */
export function AccessGate({ status, message }: { status: number; message: string }) {
  return (
    <div className={s.gate}>
      <span className="kicker ink-mute">Paceroo admin · {status === 403 ? "not an admin" : status === 503 ? "not configured" : "signed out"}</span>
      <h1 className={clsx("t-serif-lg", s.gateTitle)}>
        This console is <span style={{ fontStyle: "italic", color: "var(--accent)" }}>private.</span>
      </h1>
      <p className="t-body-md ink-soft">
        {status === 403
          ? "You signed in, but this account isn't a Paceroo admin."
          : status === 503
            ? "Admin access isn't set up for this environment yet."
            : "Sign in through Cloudflare Access to continue."}
      </p>
      <p className="t-mono-xs ink-mute">{message}</p>
    </div>
  );
}
